import { useState } from 'react';
import authStorage from '../auth_storage';

// Helper function to format date for datetime-local input
const formatDateTimeLocal = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

function SessionEditModal({ session, onClose, onSave }) {
    const [startTimestamp, setStartTimestamp] = useState(
        formatDateTimeLocal(session.startTimestamp)
    );
    const [endTimestamp, setEndTimestamp] = useState(
        session.endTimestamp && new Date(session.endTimestamp).getTime() !== 0
            ? formatDateTimeLocal(session.endTimestamp)
            : ''
    );
    const [description, setDescription] = useState(session.description || '');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validate description length
        if (description.trim().length < 80) {
            setError(`Begründung muss mindestens 80 Zeichen lang sein (aktuell: ${description.trim().length} Zeichen)`);
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`/api/v1/sessions/${session.id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'x-auth-token': authStorage.get(),
                },
                body: JSON.stringify({
                    startTimestamp,
                    endTimestamp,
                    description,
                }),
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.error?.message || 'Failed to update session');
            }

            // Show approval status message
            if (data.payload.requiresApproval) {
                alert(`✅ ${data.payload.message}\n\nDeine Änderung wurde zur Genehmigung an einen Manager/Admin weitergeleitet.`);
            } else {
                alert(`✅ ${data.payload.message}`);
            }

            onSave(data.payload.session);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const canEdit = () => {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return new Date(session.startTimestamp) >= sevenDaysAgo;
    };

    if (!canEdit()) {
        return (
            <div className="modal-overlay" onClick={onClose}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h3>Session bearbeiten</h3>
                        <button className="modal-close" onClick={onClose}>×</button>
                    </div>
                    <div className="modal-body">
                        <div className="alert alert-warning">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            Du kannst nur Sessions der letzten 7 Tage bearbeiten.
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={onClose}>
                            Schließen
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Session #{session.id} bearbeiten</h3>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                        {error && (
                            <div className="alert alert-danger mb-3">
                                <i className="bi bi-exclamation-circle-fill me-2"></i>
                                {error}
                            </div>
                        )}

                        <div className="alert alert-info mb-3">
                            <i className="bi bi-info-circle-fill me-2"></i>
                            <strong>Wichtige Hinweise:</strong>
                            <ul className="mb-0 mt-2">
                                <li>Du kannst nur die Zeiten und Beschreibung ändern</li>
                                <li>Eine ausführliche Begründung (mind. 80 Zeichen) ist erforderlich</li>
                                <li>Änderungen ≤ 15 Minuten werden sofort gespeichert</li>
                                <li>Änderungen &gt; 15 Minuten benötigen eine Genehmigung von Uta oder Mathis</li>
                            </ul>
                        </div>

                        <div className="mb-3">
                            <label className="form-label fw-semibold">
                                <i className="bi bi-clock me-2"></i>
                                Startzeit
                            </label>
                            <input
                                type="datetime-local"
                                className="form-control"
                                value={startTimestamp}
                                onChange={(e) => setStartTimestamp(e.target.value)}
                                step="1"
                                required
                            />
                            <small className="text-muted">Mit Sekunden-Genauigkeit</small>
                        </div>

                        <div className="mb-3">
                            <label className="form-label fw-semibold">
                                <i className="bi bi-clock-history me-2"></i>
                                Endzeit
                            </label>
                            <input
                                type="datetime-local"
                                className="form-control"
                                value={endTimestamp}
                                onChange={(e) => setEndTimestamp(e.target.value)}
                                step="1"
                                required
                            />
                            <small className="text-muted">Dauer wird automatisch berechnet</small>
                        </div>

                        <div className="mb-3">
                            <label className="form-label fw-semibold">
                                <i className="bi bi-card-text me-2"></i>
                                Begründung *
                            </label>
                            <textarea
                                className="form-control"
                                rows="5"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Bitte begründe die Änderung ausführlich (mindestens 80 Zeichen)..."
                                required
                            />
                            <small className={`text-${description.trim().length >= 80 ? 'success' : 'danger'}`}>
                                {description.trim().length} / 80 Zeichen
                                {description.trim().length < 80 && ` (noch ${80 - description.trim().length} Zeichen erforderlich)`}
                            </small>
                        </div>

                        <div className="session-info p-3 bg-light rounded">
                            <div className="row g-2">
                                <div className="col-6">
                                    <small className="text-muted d-block">Typ</small>
                                    <strong>{session.type}</strong>
                                </div>
                                {session.Project && (
                                    <div className="col-6">
                                        <small className="text-muted d-block">Projekt</small>
                                        <strong>{session.Project.name}</strong>
                                        {session.Subproject && (
                                            <div className="text-muted small">→ {session.Subproject.name}</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Abbrechen
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Speichern...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-check-lg me-2"></i>
                                    Änderungen speichern
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default SessionEditModal;
